import { ONE } from "./bigUtil.ts";
import { Claim, Configuration, Issue, WinChances } from "./CaseModel.ts";

/**
 * Win chances of leaf issues — the single source for both the calculation and
 * the "has the user actually entered this?" question.
 *
 * The engine has no "unknown" state: an unset chance silently resolves to a
 * certainty (0 for a generic issue, 1 otherwise). That is fine for computing,
 * but it means neither the stored value nor the rendered slider can tell a
 * deliberate 0% from a blank. {@link winChanceEntered} answers that, branch
 * for branch against {@link getWinChanceOfLeaf}, so the prerequisite check and
 * the per-issue markers can never disagree with what the engine assumed.
 */

/** Which nodes the engine treats as leaves: a claim's issues, or the claim itself. */
export function leafIssuesOfClaim(claim: Claim): Issue[] {
  if (claim.issues === undefined || claim.issues.length === 0) return [claim];
  const leaves: Issue[] = [];
  const walk = (issue: Issue) => {
    if (issue.subIssues === undefined || issue.subIssues.length === 0) {
      leaves.push(issue);
      return;
    }
    issue.subIssues.forEach(walk);
  };
  claim.issues.forEach(walk);
  return leaves;
}

/**
 * The chance entered for a defendant. Undefined both when the defendant is not
 * involved and when it is but nothing was entered yet (`null`, see
 * {@link WinChances}): the engine treats either as not running against them.
 */
export function enteredChance(
  chances: WinChances | undefined,
  defendantId: string,
): number | undefined {
  return chances?.[defendantId] ?? undefined;
}

/** The chances entered, without the unrated (`null`) defendants. */
export function enteredChances(chances: WinChances | undefined): {
  [defendantId: string]: number;
} {
  const entered: { [defendantId: string]: number } = {};
  for (const [defendantId, chance] of Object.entries(chances ?? {}))
    if (chance !== null) entered[defendantId] = chance;
  return entered;
}

export function getWinChanceOfLeaf(issue: Issue): number {
  if (issue.generic) return issue.genericWinChance || 0;

  // if there are no defendants, assume generic issue with 100% win chance
  if (issue.defendants === undefined || issue.defendants.length === 0) return 1;

  // calculate win chance based on defendants
  // if no win-chances are defined per defendant, assume 100% win chance
  if (issue.perDefendantWinChance === undefined) return 1;

  let winChance = ONE;
  for (let i = 0; i < issue.defendants.length; i++) {
    const defendantId = issue.defendants[i].id;
    const winChanceVDefendant = enteredChance(
      issue.perDefendantWinChance,
      defendantId,
    );
    if (winChanceVDefendant !== undefined)
      winChance = winChance.mul(winChanceVDefendant);
  }
  return winChance.toNumber();
}

/**
 * The win-chance fields, in the least common shape of the stored issue and the
 * editor's view model — which names the same map `winChanceAgainst` — plus who
 * the leaf runs against. Both feed the predicates below so the markers in the
 * issue table cannot drift from the gate that blocks the calculation.
 */
export interface WinChanceInput {
  generic?: boolean;
  genericWinChance?: number;
  perDefendantWinChance?: WinChances;
  // the defendants the leaf runs against
  involved: string[];
}

/**
 * True when {@link getWinChanceOfLeaf} reads a value the user entered rather
 * than falling back to a certainty. Mirrors its branches:
 *
 * - generic: `genericWinChance` set — including a deliberate 0%, which the
 *   engine's `|| 0` cannot distinguish from a blank.
 * - no defendants: nothing to enter and no input rendered for it, so the
 *   engine's 100% is the only available reading. Counted as set, otherwise the
 *   prerequisite could never be satisfied.
 * - an involved defendant without an entered chance: the engine skips it
 *   (treating the issue as a certain win against it), so anything short of
 *   every involved defendant counts as not entered.
 */
export function winChanceEntered({
  generic,
  genericWinChance,
  perDefendantWinChance,
  involved,
}: WinChanceInput): boolean {
  if (generic) return genericWinChance !== undefined;
  return involved.every(
    (id) => enteredChance(perDefendantWinChance, id) !== undefined,
  );
}

/** True when this defendant is on the issue but carries no entered chance. */
export function defendantWinChanceMissing(
  { generic, perDefendantWinChance, involved }: WinChanceInput,
  defendantId: string,
): boolean {
  if (generic) return false;
  if (!involved.includes(defendantId)) return false;
  return enteredChance(perDefendantWinChance, defendantId) === undefined;
}

/**
 * Who a leaf runs against: a claim without issues against all its defendants,
 * an issue against those its win chances name.
 */
function involvedIn(leaf: Issue, claim: Claim): string[] {
  return leaf === claim
    ? (claim.defendants ?? []).map(({ id }) => id)
    : Object.keys(leaf.perDefendantWinChance ?? {});
}

/** The leaves of a claim still missing an entered win chance. */
export function leavesMissingWinChanceOf(claim: Claim): Issue[] {
  return leafIssuesOfClaim(claim).filter(
    (leaf) =>
      !winChanceEntered({
        generic: leaf.generic,
        genericWinChance: leaf.genericWinChance,
        perDefendantWinChance: leaf.perDefendantWinChance,
        involved: involvedIn(leaf, claim),
      }),
  );
}

/** Every leaf across the case still missing an entered win chance. */
export function leavesMissingWinChance(claims: Claim[] | undefined): Issue[] {
  return (claims ?? []).flatMap(leavesMissingWinChanceOf);
}

/**
 * The win chances a what-if (the bargaining variant) varies: those of every
 * leaf, keyed by its id — each issue, or the claim itself where it has none.
 * Leaves keep their structure: a generic issue stays one chance for all its
 * defendants, so varying it moves them together, as the calculation decides it.
 */
export function winChancesOfLeaves(
  claims: Claim[] | undefined,
): Configuration["winChances"] {
  const winChances: Configuration["winChances"] = {};
  (claims ?? []).flatMap(leafIssuesOfClaim).forEach((leaf) => {
    winChances[leaf.id] = {
      generic: leaf.generic === true,
      genericWinChance: leaf.genericWinChance ?? 0,
      // entered chances only: an unrated defendant is not part of a calculation
      perDefendantWinChance: enteredChances(leaf.perDefendantWinChance),
    };
  });
  return winChances;
}

/**
 * Sets the chances of a what-if on the leaves of the claims. A what-if changes
 * chances, never the structure: a leaf keeps its kind and who it runs against,
 * so a chance stored for the other kind, or for a defendant the leaf does not
 * run against, is not applied.
 */
export function applyWinChances(
  claims: Claim[] | undefined,
  winChances: Configuration["winChances"],
): void {
  (claims ?? []).flatMap(leafIssuesOfClaim).forEach((leaf) => {
    const winChance = winChances[leaf.id];
    if (
      winChance === undefined ||
      winChance.generic !== (leaf.generic === true)
    )
      return;
    if (leaf.generic) {
      leaf.genericWinChance = winChance.genericWinChance;
      return;
    }
    const base = leaf.perDefendantWinChance ?? {};
    const overrides = Object.entries(winChance.perDefendantWinChance).filter(
      ([defendantId]) => defendantId in base,
    );
    leaf.perDefendantWinChance = { ...base, ...Object.fromEntries(overrides) };
  });
}
